import { Component } from '@theme/component';

/**
 * @typedef {object} CardVariant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} url
 * @property {string} price - Formatted price.
 * @property {string} compare - Formatted compare-at price, empty when not on sale.
 * @property {string} discount - Discount badge text, empty when not on sale.
 */

/**
 * A ZAYA product card. Color swatches switch the card image, price, and link, and choose the color that add
 * to cart adds. For products with sizes, the size radios are required members of the add to cart form, so
 * pressing add to cart without a size fails validation; the card then opens its size panel, and tapping a
 * size adds it.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} [image] - The primary card image.
 * @property {HTMLAnchorElement[]} links - Links to the product.
 * @property {HTMLButtonElement[]} [swatches] - Color swatch buttons.
 * @property {HTMLElement} [sizePanel] - Size choice panel over the image.
 * @property {HTMLInputElement[]} [sizeInputs] - Size radios. Each value is the variant id for the shown color and that size.
 * @property {HTMLScriptElement} [variantData] - JSON list of the product's variants.
 * @property {HTMLElement} discountBadge - The sale percentage badge.
 * @property {HTMLElement} compare - Wrapper for the compare-at price and its labels.
 * @property {HTMLElement} comparePrice - The compare-at price text.
 * @property {HTMLElement} currentPrice - Wrapper for the current price.
 * @property {HTMLElement} currentPriceText - The current price text.
 *
 * @extends Component<Refs>
 */
class ZayaProductCard extends Component {
  requiredRefs = ['links', 'discountBadge', 'compare', 'comparePrice', 'currentPrice', 'currentPriceText'];

  /** @type {CardVariant[] | null} */
  #variants = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** Whether the latest size choice came from a pointer, which adds to the cart straight away. */
  #pointerSizeChoice = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // `invalid` doesn't bubble, so listen in the capture phase to see it on the required size radios.
    this.addEventListener('invalid', this.#handleInvalid, { capture: true, signal });
    this.addEventListener('submit', this.#handleSubmitted, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = null;
  }

  get #colorIndex() {
    return this.dataset.colorIndex === undefined ? -1 : Number(this.dataset.colorIndex);
  }

  get #sizeIndex() {
    return this.dataset.sizeIndex === undefined ? -1 : Number(this.dataset.sizeIndex);
  }

  /** @returns {CardVariant[]} */
  get #variantList() {
    if (this.#variants) return this.#variants;

    try {
      this.#variants = JSON.parse(this.refs.variantData?.textContent ?? '[]');
    } catch {
      this.#variants = [];
    }
    return this.#variants ?? [];
  }

  get #selectedColor() {
    const pressed = this.refs.swatches?.find((swatch) => swatch.getAttribute('aria-pressed') === 'true');
    return pressed?.dataset.value ?? null;
  }

  get #selectedSize() {
    return this.refs.sizeInputs?.find((input) => input.checked)?.dataset.size ?? null;
  }

  get #addButton() {
    const button = this.querySelector('product-form-component button[name="add"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Shows the color of the clicked swatch on the card.
   * @param {MouseEvent} event - The click on a swatch button.
   */
  selectColor(event) {
    const swatch = event.target;
    if (!(swatch instanceof HTMLButtonElement)) return;

    for (const button of this.refs.swatches ?? []) {
      button.setAttribute('aria-pressed', String(button === swatch));
    }

    const { image } = this.refs;
    const { imageSrc, imageSrcset } = swatch.dataset;
    if (image && imageSrc) {
      if (imageSrcset) image.srcset = imageSrcset;
      image.src = imageSrc;
    }

    // The hover image belongs to the default color, so stop revealing it once a color is chosen.
    this.dataset.colorSelected = 'true';

    this.#update();
  }

  /**
   * Records whether a size was picked with a pointer. Keyboard arrows also check radios, and those choices
   * shouldn't add to the cart until the shopper presses add to cart.
   * @param {MouseEvent} event - The click on a size radio.
   */
  handleSizeClick(event) {
    this.#pointerSizeChoice = event.detail > 0;
  }

  /**
   * Updates the card after a size is chosen, and adds it to the cart when it was tapped.
   * @param {Event} event - The change on a size radio.
   */
  selectSize(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    this.#update();

    if (!this.#pointerSizeChoice) return;
    this.#pointerSizeChoice = false;
    this.#addButton?.click();
  }

  /** Closes the size panel from its close button. */
  closeSizes() {
    this.#closeSizes();
  }

  /**
   * Closes the size panel on Escape.
   * @param {KeyboardEvent} event - The keydown inside the size panel.
   */
  handleSizePanelKeydown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#closeSizes();
  }

  /**
   * Add to cart without a size fails the form's required size radios; ask for a size instead.
   * @param {Event} event - The invalid event on a size radio.
   */
  #handleInvalid = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLInputElement) || !this.refs.sizeInputs?.includes(target)) return;

    // The browser's own message can't point at a radio inside the closed panel.
    event.preventDefault();
    this.#openSizes();
  };

  /** Runs after the product form has read the chosen size: clears it so the next add asks again. */
  #handleSubmitted = () => {
    const { sizeInputs = [] } = this.refs;
    if (sizeInputs.length === 0) return;

    for (const input of sizeInputs) input.checked = false;
    this.#closeSizes();
  };

  #openSizes() {
    const { sizePanel, sizeInputs = [] } = this.refs;
    if (!sizePanel) return;

    sizePanel.hidden = false;
    sizeInputs.find((input) => !input.disabled)?.focus({ preventScroll: true });
  }

  /** Hides the size panel, moving focus to add to cart when it was inside the panel. */
  #closeSizes() {
    const { sizePanel } = this.refs;
    if (!sizePanel || sizePanel.hidden) return;

    const hadFocus = sizePanel.contains(document.activeElement);
    sizePanel.hidden = true;
    if (hadFocus) this.#addButton?.focus();
  }

  /**
   * Finds the variant for the chosen color and size. Without a chosen size, falls back to the first
   * available variant in the chosen color so the price and link still follow the color.
   * @returns {{ variant: CardVariant | undefined, exact: boolean }}
   */
  #resolveVariant() {
    const color = this.#selectedColor;
    const size = this.#selectedSize;
    const sizeIndex = this.#sizeIndex;
    const inColor = this.#variantsInColor(color);

    if (sizeIndex >= 0 && size !== null) {
      return { variant: inColor.find((variant) => variant.options[sizeIndex] === size), exact: true };
    }

    return {
      variant: inColor.find((variant) => variant.available) ?? inColor[0],
      exact: sizeIndex < 0,
    };
  }

  /**
   * @param {string | null} color
   * @returns {CardVariant[]}
   */
  #variantsInColor(color) {
    const colorIndex = this.#colorIndex;
    return this.#variantList.filter(
      (variant) => colorIndex < 0 || color === null || variant.options[colorIndex] === color
    );
  }

  #update() {
    this.#updateSizes();

    const { variant, exact } = this.#resolveVariant();
    if (variant) this.#showVariant(variant);
    this.#updateBuyButton(variant, exact);
  }

  /**
   * Points each size radio at the chosen color's variant for that size, disables sizes that are
   * unavailable in the color, and clears a chosen size that became unavailable.
   */
  #updateSizes() {
    const { sizeInputs = [] } = this.refs;
    const sizeIndex = this.#sizeIndex;
    if (sizeIndex < 0) return;

    const inColor = this.#variantsInColor(this.#selectedColor);
    for (const input of sizeInputs) {
      const match = inColor.find((variant) => variant.options[sizeIndex] === input.dataset.size);
      input.value = match ? String(match.id) : '';
      input.disabled = !match?.available;
      if (input.disabled) input.checked = false;
    }
  }

  /** @param {CardVariant} variant */
  #showVariant(variant) {
    const { links, discountBadge, compare, comparePrice, currentPrice, currentPriceText } = this.refs;
    const onSale = variant.compare !== '';

    for (const link of links) link.href = variant.url;

    currentPriceText.textContent = variant.price;
    currentPrice.classList.toggle('zaya-card__current--sale', onSale);
    compare.hidden = !onSale;
    if (onSale) comparePrice.textContent = variant.compare;

    discountBadge.hidden = !variant.discount;
    if (variant.discount) discountBadge.textContent = variant.discount;
  }

  /**
   * Sets the add to cart button's state and label, and keeps the form's variant input on the resolved
   * variant. With sizes, the button only reflects whether the chosen color has stock in any size.
   * @param {CardVariant | undefined} variant
   * @param {boolean} exact - Whether the variant matches every option the card asks for.
   */
  #updateBuyButton(variant, exact) {
    const idInput = this.querySelector('product-form-component input[ref="variantId"]');
    const button = this.#addButton;
    const label = this.querySelector('.zaya-card__buy-label');
    if (!(idInput instanceof HTMLInputElement) || !button) return;

    const { addToCart, soldOut } = this.#labels;

    if (this.#sizeIndex >= 0) {
      const inStock = this.#variantsInColor(this.#selectedColor).some((option) => option.available);
      if (variant && exact) idInput.value = String(variant.id);
      button.disabled = !inStock;
      if (label) label.textContent = inStock ? addToCart : soldOut;
      if (!inStock) this.#closeSizes();
      return;
    }

    if (!variant) {
      button.disabled = true;
      if (label) label.textContent = soldOut;
      return;
    }

    idInput.value = String(variant.id);
    button.disabled = !variant.available;
    if (label) label.textContent = variant.available ? addToCart : soldOut;
  }

  get #labels() {
    return {
      addToCart: this.dataset.labelAdd ?? '',
      soldOut: this.dataset.labelSoldOut ?? '',
    };
  }
}

if (!customElements.get('zaya-product-card')) {
  customElements.define('zaya-product-card', ZayaProductCard);
}
